import React from "react";
import { Pencil } from "lucide-react";
import { US_STATES } from "@/lib/states";
import {
  GOAL_OPTIONS,
  PRODUCT_INTERESTS,
  MARITAL_OPTIONS,
  TIME_HORIZON_OPTIONS,
  RISK_OPTIONS,
} from "@/lib/applyForm";

const labelOf = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";

const formatUSD = (n) =>
  n === "" || n === null || n === undefined
    ? "—"
    : `$${Number(n).toLocaleString()}`;

const SectionHeader = ({ title, onEdit, testId }) => (
  <div className="flex items-center justify-between mb-3">
    <span className="lw-eyebrow">{title}</span>
    <button
      type="button"
      onClick={onEdit}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-lw-navy hover:text-[#8a6a2b] transition-colors"
    >
      <Pencil size={14} /> Edit
    </button>
  </div>
);

const Row = ({ label, value, testId }) => (
  <div
    className="flex items-baseline justify-between gap-4 py-1.5"
    data-testid={testId}
  >
    <span className="text-sm text-lw-muted">{label}</span>
    <span className="font-serif-display text-base text-lw-navy text-right break-words max-w-[60%]">
      {value || "—"}
    </span>
  </div>
);

export const Review = ({ form, onEditStep }) => {
  const { life_stage_data: ls, financial_data: fin, product_interests: pi, contact } = form;
  return (
    <div data-testid="apply-review" className="space-y-8">
      <div>
        <SectionHeader
          title="Life Stage"
          onEdit={() => onEditStep(1)}
          testId="apply-edit-step-1"
        />
        <div className="rounded-xl border border-lw-navy/10 bg-white p-4 sm:p-5">
          <Row label="Age" value={ls.age} testId="review-age" />
          <Row label="Marital status" value={labelOf(MARITAL_OPTIONS, ls.marital_status)} />
          <Row label="Dependents" value={ls.dependents} />
          <Row
            label="Goals"
            value={
              ls.goals.length
                ? ls.goals.map((g) => labelOf(GOAL_OPTIONS, g)).join(", ")
                : "—"
            }
          />
          <Row label="Time horizon" value={labelOf(TIME_HORIZON_OPTIONS, ls.time_horizon)} />
        </div>
      </div>

      <div>
        <SectionHeader
          title="Financial Snapshot"
          onEdit={() => onEditStep(2)}
          testId="apply-edit-step-2"
        />
        <div className="rounded-xl border border-lw-navy/10 bg-white p-4 sm:p-5">
          <Row label="Annual income" value={formatUSD(fin.income)} testId="review-income" />
          <Row label="Retirement savings" value={formatUSD(fin.retirement_savings)} />
          <Row label="Existing life coverage" value={formatUSD(fin.existing_life_coverage)} />
          <Row label="Total debt" value={formatUSD(fin.debt)} />
          <Row label="Monthly savings capacity" value={formatUSD(fin.monthly_savings)} />
          <Row label="Risk comfort" value={labelOf(RISK_OPTIONS, fin.risk_comfort)} />
        </div>
      </div>

      <div>
        <SectionHeader
          title="Product Interests"
          onEdit={() => onEditStep(3)}
          testId="apply-edit-step-3"
        />
        <div className="rounded-xl border border-lw-navy/10 bg-white p-4 sm:p-5">
          {pi.length ? (
            <div className="flex flex-wrap gap-2">
              {pi.map((p) => (
                <span
                  key={p}
                  className="lw-pill"
                  data-testid={`review-interest-${p}`}
                >
                  {labelOf(PRODUCT_INTERESTS, p)}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-sm text-lw-muted">None selected.</span>
          )}
        </div>
      </div>

      <div>
        <SectionHeader
          title="Contact"
          onEdit={() => onEditStep(4)}
          testId="apply-edit-step-4"
        />
        <div className="rounded-xl border border-lw-navy/10 bg-white p-4 sm:p-5">
          <Row label="Name" value={contact.full_name} testId="review-name" />
          <Row label="Email" value={contact.email} testId="review-email" />
          <Row label="Phone" value={contact.phone} />
          <Row label="State" value={labelOf(US_STATES, contact.state)} />
          <Row label="Best time" value={contact.best_time} />
          <Row label="Preferred method" value={contact.contact_method} />
          {contact.notes && <Row label="Notes" value={contact.notes} />}
        </div>
      </div>
    </div>
  );
};

export default Review;
